import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { searchEverything } from "@/api/search";

/**
 * Whether the top-bar search has anything worth asking the server. Anything
 * shorter than 3 letters is an empty result with no query — except digits (a
 * customer or champ number, or the start of a phone number), and CX- / CH-
 * numbers, which are exact lookups from the first character.
 */
export function isSearchable(q: string): boolean {
  const text = q.trim();
  if (!text) return false;
  if (/^\d+$/.test(text)) return true;
  if (/^(cx|ch)-?\d+$/i.test(text)) return true;
  return text.length >= 3;
}

export function useSearch(q: string) {
  const text = q.trim();
  return useQuery({
    queryKey: ["search", text] as const,
    queryFn: ({ signal }) => searchEverything(text, signal),
    enabled: isSearchable(text),
    // A 403 or 404 is an answer, never retried.
    retry: (failureCount, error) =>
      error instanceof ApiError && (error.status === 403 || error.status === 404)
        ? false
        : failureCount < 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
